import { forwardRef, useCallback, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { DocumentNode } from "graphql/index";
import { GroupBase, OptionsOrGroups, SelectInstance } from "react-select";
import { LoadOptions } from "react-select-async-paginate";
import { usePrevious } from "../../../core/utils";
import { RefType } from "../../../types";
import { AsyncSelect, AsyncSelectProps } from "../../../ui";
import { genericMemo } from "../../../utils";
import {
  FilterExpressionChildInput,
  FilterExpressionOperatorEnum,
  FilterOperatorEnum,
  GraphQLPageable,
  GraphQLPageableQueryResult,
  Maybe,
} from "../../types";
import { extractPageableResult } from "../../utils";
import { buildRequestFilter, buildRequestOrder } from "../builders";
import { GraphQLFilteringProps, GraphQLOrderProps, RequestFilterParams } from "../types";

export type GraphQLSelectProps<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Additional = {},
  Group extends GroupBase<Option> = GroupBase<Option>,
  R extends SelectInstance<Option, IsMulti, Group> = SelectInstance<Option, IsMulti, Group>
> = Omit<AsyncSelectProps<Option, IsMulti, Additional, Group, R>, "loadOptions"> & {
  loadOptions?: LoadOptions<Option, Group, Additional>,
  graphQLQuery?: DocumentNode,
  graphQLExtractQueryData?: (data: GraphQLPageableQueryResult<Option>) => GraphQLPageable<Option>,
  graphQLPageSize?: number,
  graphQLGlobalFilters?: Maybe<FilterExpressionChildInput>,
} & GraphQLFilteringProps<Option> & GraphQLOrderProps<Option>

const GraphQLSelectComponent = <
  Option = unknown,
  IsMulti extends boolean = boolean,
  Additional = {},
  Group extends GroupBase<Option> = GroupBase<Option>,
  R extends SelectInstance<Option, IsMulti, Group> = SelectInstance<Option, IsMulti, Group>
>(props: GraphQLSelectProps<Option, IsMulti, Additional, Group, R>) => <></>;
export type GraphQLSelectComponentType = typeof GraphQLSelectComponent;

export const GraphQLSelect = genericMemo(forwardRef(<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Additional = {},
  Group extends GroupBase<Option> = GroupBase<Option>,
  R extends SelectInstance<Option, IsMulti, Group> = SelectInstance<Option, IsMulti, Group>
>(
  {
    graphQLQuery,
    graphQLExtractQueryData = extractPageableResult,
    graphQLPageSize = 20,
    graphQLGlobalFilters,
    graphQLFilterPath,
    graphQLFilterOperator = FilterOperatorEnum.Equal,
    graphQLFilterValueModifier,
    graphQLFilterExpressionBuilder,
    graphQLOrder,
    onMenuOpen,
    onMenuClose,
    ...props
  }: GraphQLSelectProps<Option, IsMulti, Additional, Group, R>,
  ref: RefType<R>,
) => {
  const [ isMenuOpen, setMenuOpen ] = useState(props.menuIsOpen);
  const graphQLClient = useApolloClient();
  const orders = useMemo(() => buildRequestOrder(graphQLOrder), [ graphQLOrder ]);
  const realGraphQLFilterPath = graphQLFilterPath?.toString();

  const searchFilterBuilder = useCallback((value: string): RequestFilterParams[] | undefined => {
    return (realGraphQLFilterPath || graphQLFilterExpressionBuilder) ? [ {
      path: (realGraphQLFilterPath || "") as string,
      operator: graphQLFilterOperator,
      value: graphQLFilterValueModifier ? graphQLFilterValueModifier?.(value) : value,
      expressionBuilder: graphQLFilterExpressionBuilder
        ? (v) => graphQLFilterExpressionBuilder(v)
        : undefined,
    } ] : undefined;
  }, [ graphQLFilterExpressionBuilder, graphQLFilterOperator, realGraphQLFilterPath, graphQLFilterValueModifier ]);

  const [ loadOptions, key ] = useMemo((): [ LoadOptions<Option, Group, Additional & { page: number }>, number ] => [
    async (inputValue, loadedOptions, additional) => {
      const page = additional?.page || 0;

      if (!graphQLQuery) {
        throw new Error("graphQLQuery is required for GraphQLSelect");
      }

      const filter = graphQLGlobalFilters
        ? {
          expr: {
            operator: FilterExpressionOperatorEnum.And,
            children: [
              graphQLGlobalFilters,
              buildRequestFilter(searchFilterBuilder(inputValue)),
            ].filter(x => x),
          },
        }
        : buildRequestFilter(searchFilterBuilder(inputValue));

      const response = await graphQLClient.query({
        query: graphQLQuery,
        variables: {
          ...additional,
          page,
          size: graphQLPageSize,
          orders,
          filter,
        },
      });
      const queryData = graphQLExtractQueryData?.(response?.data);

      return ({
        options: (queryData?.items || []) as OptionsOrGroups<Option, Group>,
        hasMore: (queryData?.totalPages || 1) > (page + 1),
        additional: {
          ...additional,
          page: page + 1,
        } as Additional & { page: number },
      });
    },
    Date.now(),
  ], [ graphQLClient, graphQLExtractQueryData, graphQLGlobalFilters, graphQLPageSize, graphQLQuery, orders, searchFilterBuilder ]);

  const previousKey = usePrevious(key);

  return (
    <AsyncSelect<Option, IsMulti, Additional, Group, R>
      cacheUniqs={[ key ]}
      defaultOptions={isMenuOpen && previousKey !== undefined && previousKey !== key}
      {...props}
      loadOptions={props?.loadOptions ? props.loadOptions : loadOptions as LoadOptions<Option, Group, Additional>}
      onMenuOpen={useCallback(() => {
        setMenuOpen(true);
        onMenuOpen?.();
      }, [ onMenuOpen ])}
      onMenuClose={useCallback(() => {
        setMenuOpen(false);
        onMenuClose?.();
      }, [ onMenuClose ])}
      ref={ref}
    />
  );
}) as GraphQLSelectComponentType);